// src/api/gmail.js
// Gmail API Service for searching and reading emails

import { getStoredTokens, refreshAccessToken, getGoogleAuthUrl } from './googleDrive';

const GMAIL_API_BASE = 'https://www.googleapis.com/gmail/v1';

/**
 * Get valid access token (refresh if needed)
 */
const getValidAccessToken = async () => {
  let tokens = getStoredTokens();

  if (!tokens) {
    throw new Error('NOT_AUTHENTICATED:' + getGoogleAuthUrl());
  }

  if (tokens.needsRefresh) {
    tokens = await refreshAccessToken(tokens.refresh_token);
  }

  return tokens.access_token;
};

/**
 * Search emails by query
 * @param {string} query - Gmail search query (e.g., "from:client@email.com" or "subject:booking")
 * @param {number} maxResults - Maximum number of results (default 10)
 */
export const searchEmails = async (query, maxResults = 10) => {
  const accessToken = await getValidAccessToken();

  const params = new URLSearchParams({
    q: query,
    maxResults: maxResults.toString(),
  });

  const response = await fetch(
    `${GMAIL_API_BASE}/users/me/messages?${params}`,
    {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to search emails');
  }

  const data = await response.json();
  return data.messages || [];
};

/**
 * Get email thread by ID
 * @param {string} threadId - Thread ID
 */
export const getThread = async (threadId) => {
  const accessToken = await getValidAccessToken();

  const response = await fetch(
    `${GMAIL_API_BASE}/users/me/threads/${threadId}?format=full`,
    {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to get thread');
  }

  return await response.json();
};

/**
 * Get email message by ID
 * @param {string} messageId - Message ID
 */
export const getMessage = async (messageId) => {
  const accessToken = await getValidAccessToken();

  const response = await fetch(
    `${GMAIL_API_BASE}/users/me/messages/${messageId}?format=full`,
    {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to get message');
  }

  return await response.json();
};

/**
 * Search threads by query (groups messages by conversation)
 * @param {string} query - Gmail search query
 * @param {number} maxResults - Maximum number of results
 */
export const searchThreads = async (query, maxResults = 10) => {
  const accessToken = await getValidAccessToken();

  const params = new URLSearchParams({
    q: query,
    maxResults: maxResults.toString(),
  });

  const response = await fetch(
    `${GMAIL_API_BASE}/users/me/threads?${params}`,
    {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to search threads');
  }

  const data = await response.json();
  return data.threads || [];
};

/**
 * Extract header value from message
 */
const getHeader = (headers, name) => {
  const header = headers.find(h => h.name.toLowerCase() === name.toLowerCase());
  return header ? header.value : null;
};

/**
 * Decode base64 email body
 */
const decodeBody = (body) => {
  if (!body || !body.data) return '';

  try {
    // Gmail uses URL-safe base64, convert to standard base64
    const base64 = body.data.replace(/-/g, '+').replace(/_/g, '/');
    return decodeURIComponent(escape(atob(base64)));
  } catch (e) {
    console.error('Error decoding body:', e);
    return '';
  }
};

/**
 * Extract text content from message parts
 */
const extractTextFromParts = (parts) => {
  let text = '';

  for (const part of parts) {
    if (part.mimeType === 'text/plain' && part.body?.data) {
      text += decodeBody(part.body);
    } else if (part.mimeType === 'text/html' && part.body?.data && !text) {
      // Fallback to HTML if no plain text
      const html = decodeBody(part.body);
      // Basic HTML to text conversion
      text = html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n\n')
        .replace(/<\/div>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim();
    } else if (part.parts) {
      text += extractTextFromParts(part.parts);
    }
  }

  return text;
};

/**
 * Parse message into a readable format
 */
export const parseMessage = (message) => {
  const headers = message.payload?.headers || [];

  let body = '';

  // Try to get body from payload directly
  if (message.payload?.body?.data) {
    body = decodeBody(message.payload.body);
  }
  // Or from parts
  else if (message.payload?.parts) {
    body = extractTextFromParts(message.payload.parts);
  }

  return {
    id: message.id,
    threadId: message.threadId,
    from: getHeader(headers, 'From'),
    to: getHeader(headers, 'To'),
    subject: getHeader(headers, 'Subject'),
    date: getHeader(headers, 'Date'),
    snippet: message.snippet,
    body: body,
    labels: message.labelIds || [],
  };
};

/**
 * Parse thread into readable format with all messages
 */
export const parseThread = (thread) => {
  const messages = (thread.messages || []).map(parseMessage);

  // Sort messages by date (oldest first)
  messages.sort((a, b) => new Date(a.date) - new Date(b.date));

  return {
    id: thread.id,
    subject: messages[0]?.subject || 'No subject',
    snippet: thread.snippet,
    messageCount: messages.length,
    messages: messages,
    latestDate: messages[messages.length - 1]?.date,
  };
};

/**
 * Search and get full thread details for a client name
 * @param {string} clientName - Client name to search for
 * @param {number} maxResults - Max threads to return
 */
export const searchClientThreads = async (clientName, maxResults = 5) => {
  try {
    // Search for threads containing the client name
    const threads = await searchThreads(clientName, maxResults);

    // Get full details for each thread
    const fullThreads = await Promise.all(
      threads.map(async (thread) => {
        const fullThread = await getThread(thread.id);
        return parseThread(fullThread);
      })
    );

    return fullThreads;
  } catch (error) {
    console.error('Error searching client threads:', error);
    throw error;
  }
};

export default {
  searchEmails,
  searchThreads,
  getThread,
  getMessage,
  parseMessage,
  parseThread,
  searchClientThreads,
};
